import { useEffect, useState } from "react";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { inr } from "../lib/format";

const TABS = [
  { id: "income", label: "Income" },
  { id: "category", label: "Categories" },
  { id: "business", label: "Business" },
  { id: "cashflow", label: "Cash flow" },
  { id: "cards", label: "Cards" },
];

const downloadCsv = async (path, filename) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { credentials: "include" });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Export failed");
  }
};

const Table = ({ rows, cols }) => (
  <div className="bg-white border border-border rounded-2xl overflow-hidden">
    {rows.length === 0 ? (
      <p className="py-8 text-sm text-muted-foreground text-center">No data in this range.</p>
    ) : (
      rows.map((r, i) => (
        <div key={i} className="flex justify-between items-center px-4 py-3 border-b border-border last:border-0 text-sm gap-2">
          {cols.map((c, j) => (
            <span key={j} className={j === 0 ? "flex-1 font-medium truncate" : "tnum text-right shrink-0" + (c.cls ? ` ${c.cls}` : "")}>
              {c.fmt ? c.fmt(r[c.key]) : r[c.key]}
            </span>
          ))}
        </div>
      ))
    )}
  </div>
);

export default function Reports() {
  const [tab, setTab] = useState("income");
  const [from, setFrom] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/${tab}?date_from=${from}&date_to=${to}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tab, from, to]);

  const preset = (p) => {
    const now = new Date();
    if (p === "this") { setFrom(format(startOfMonth(now), "yyyy-MM-dd")); setTo(format(endOfMonth(now), "yyyy-MM-dd")); }
    if (p === "last") { const lm = subMonths(now, 1); setFrom(format(startOfMonth(lm), "yyyy-MM-dd")); setTo(format(endOfMonth(lm), "yyyy-MM-dd")); }
    if (p === "fy") {
      const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      setFrom(`${y}-04-01`); setTo(`${y + 1}-03-31`);
    }
  };

  const amt = { key: "amount", fmt: inr };

  return (
    <div className="px-5 pt-8" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold tracking-tight">Reports</h1>
        <button data-testid="report-export-btn" onClick={() => downloadCsv(`/export/reports/${tab}?date_from=${from}&date_to=${to}`, `${tab}.csv`)}
          className="h-11 px-4 rounded-xl border border-border bg-white flex items-center gap-2 text-sm font-medium">
          <DownloadSimple size={16} /> CSV
        </button>
      </div>

      <div className="mt-4 flex gap-2">
        {[["this", "This month"], ["last", "Last month"], ["fy", "This FY"]].map(([k, v]) => (
          <button key={k} data-testid={`report-preset-${k}`} onClick={() => preset(k)} className="h-9 px-3 rounded-full bg-white border border-border text-xs font-medium">{v}</button>
        ))}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input data-testid="report-date-from" type="date" className="h-11 px-3 rounded-xl bg-white border border-border text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
        <input data-testid="report-date-to" type="date" className="h-11 px-3 rounded-xl bg-white border border-border text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto -mx-5 px-5 pb-1">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`report-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`shrink-0 h-10 px-4 rounded-full text-sm font-medium border ${tab === t.id ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-muted-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="py-10 flex justify-center"><div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : !data ? null : tab === "income" ? (
          <>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">By member</p>
              <Table rows={data.by_member} cols={[{ key: "name" }, amt]} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">By source</p>
              <Table rows={data.by_source} cols={[{ key: "name" }, amt]} />
            </div>
          </>
        ) : tab === "cashflow" ? (
          <div className="bg-white border border-border rounded-2xl overflow-hidden">
            {data.rows.length === 0 ? <p className="py-8 text-sm text-muted-foreground text-center">No data in this range.</p> :
              data.rows.map((r, i) => (
                <div key={i} className="px-4 py-3 border-b border-border last:border-0">
                  <div className="flex justify-between text-sm"><span className="font-medium">{r.name}</span><span className={`tnum font-semibold ${r.net >= 0 ? "text-income" : "text-expense"}`}>{inr(r.net)}</span></div>
                  <p className="text-xs text-muted-foreground mt-0.5">In {inr(r.inflow)} · Out {inr(r.outflow)}</p>
                </div>
              ))}
          </div>
        ) : tab === "cards" ? (
          <div className="bg-white border border-border rounded-2xl overflow-hidden">
            {data.rows.length === 0 ? <p className="py-8 text-sm text-muted-foreground text-center">No credit cards yet.</p> :
              data.rows.map((r, i) => (
                <div key={i} className="px-4 py-3 border-b border-border last:border-0">
                  <div className="flex justify-between text-sm"><span className="font-medium">{r.name}</span><span className="tnum font-semibold text-expense">{inr(r.outstanding)} due</span></div>
                  <p className="text-xs text-muted-foreground mt-0.5">Purchases {inr(r.purchases)} · Repaid {inr(r.repayments)}</p>
                </div>
              ))}
          </div>
        ) : (
          <Table rows={data.rows} cols={[{ key: "name" }, amt]} />
        )}
      </div>
      <div className="h-6" />
    </div>
  );
}
